import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import Modal from '../../../components/Modal/Modal';
import { checkDunningCharge } from '../../../services/finance/dunningApi';
import {
   ControlTextarea,
   FieldHint,
   FieldLabel,
   PrimaryActionButton,
   SecondaryButton,
} from '../../../styles/dunning/base';
import { ModalText } from '../../../styles/dunning/modals';
import { financeErrorMessage } from '../utils/apiError';
import { formatBRL, formatDateBR } from '../utils/dunningFormat';

// ---------------------------------------------------------------------------
// Conferência de título no acerto com o escritório (handoff 0103 §5.4).
//
// Vive em `pages/Finance/components/` porque tem DOIS consumidores com shapes
// de dado diferentes: a tabela de títulos do caso (Dunning/CaseDrawer, onde o
// título é um `member`) e o relatório do acerto (DunningReconciliation, onde a
// linha já vem achatada). Por isso a API é normalizada — quem chama traduz o
// próprio shape, e o modal não conhece nenhum dos dois.
//
// Só vale para título SETTLED de caso da trilha EXTERNAL — e funciona TAMBÉM em
// caso encerrado: o acerto acontece depois do pagamento, quando o caso já
// fechou.
//
// Permissão `finance:dunning:reconcile`, deliberadamente separada de
// `:interact`: quem trata o caso — inclusive o operador do próprio escritório —
// não confere o próprio trabalho. O gate é do chamador.
//
// Desmarcar não pede motivo: a nota descreve a conferência, e desfazer é
// correção de clique. O evento fica na timeline do caso de qualquer forma.
// ---------------------------------------------------------------------------

// Espelha o limite do backend (`note` ≤ 255).
export const CHECK_NOTE_MAX = 255;

export default function CheckChargeModal({
   isOpen,
   dunningId = null,
   chargeId = null,
   checked,
   label = null,
   value = null,
   paymentDate = null,
   onClose,
   onConfirmed,
}) {
   const [note, setNote] = useState('');
   const [isSaving, setIsSaving] = useState(false);

   // A ação é sempre o INVERSO do estado atual do título.
   const isChecking = !checked;

   // Rascunho limpo a cada abertura — a nota é de UMA conferência, não herda.
   useEffect(() => {
      if (isOpen) setNote('');
   }, [isOpen]);

   const handleConfirm = async () => {
      if (!dunningId || !chargeId) return;
      setIsSaving(true);
      try {
         const updatedCase = await checkDunningCharge(dunningId, chargeId, {
            checked: isChecking,
            note: isChecking ? note.trim() : undefined,
         });
         toast.success(
            isChecking ? 'Título conferido.' : 'Conferência desfeita.'
         );
         // Responde o CASO inteiro (como toda mutação do módulo) — o drawer
         // aplica direto; o relatório ignora e refaz a busca, porque a linha
         // pode ter saído do recorte filtrado.
         onConfirmed(updatedCase);
         onClose();
      } catch (err) {
         // Modal segue aberto: a nota digitada não se perde.
         toast.error(
            financeErrorMessage(
               err,
               isChecking
                  ? 'Não foi possível conferir o título.'
                  : 'Não foi possível desfazer a conferência.'
            )
         );
      } finally {
         setIsSaving(false);
      }
   };

   const subtitle = [
      label,
      value !== null ? formatBRL(value) : null,
      paymentDate ? `pago em ${formatDateBR(paymentDate)}` : null,
   ]
      .filter(Boolean)
      .join(' · ');

   let confirmLabel = isChecking ? 'Marcar conferido' : 'Desfazer';
   if (isSaving) confirmLabel = 'Salvando...';

   return (
      <Modal
         isOpen={isOpen}
         title={isChecking ? 'Conferir título' : 'Desfazer conferência'}
         subtitle={subtitle || null}
         onClose={onClose}
         maxWidth="460px"
         footer={
            <>
               <SecondaryButton type="button" onClick={onClose}>
                  Cancelar
               </SecondaryButton>
               <PrimaryActionButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSaving}
               >
                  {confirmLabel}
               </PrimaryActionButton>
            </>
         }
      >
         <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <ModalText>
               {isChecking
                  ? 'Marque o título como conferido depois de bater o pagamento com o relatório do escritório. Ele passa a contar no total do acerto.'
                  : 'O título volta para a lista de não conferidos e sai do total do acerto.'}
            </ModalText>

            {isChecking && (
               <div>
                  <FieldLabel htmlFor="dunning-check-note">
                     Observação da conferência
                  </FieldLabel>
                  <ControlTextarea
                     id="dunning-check-note"
                     rows={2}
                     maxLength={CHECK_NOTE_MAX}
                     placeholder="Ex.: Bateu com o relatório de agosto"
                     value={note}
                     onChange={(event) => setNote(event.target.value)}
                  />
                  <FieldHint>
                     Opcional, até {CHECK_NOTE_MAX} caracteres. Aparece no
                     relatório do acerto.
                  </FieldHint>
               </div>
            )}
         </div>
      </Modal>
   );
}

CheckChargeModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   dunningId: PropTypes.number,
   chargeId: PropTypes.number,
   // Estado ATUAL do título — a ação é o inverso dele.
   checked: PropTypes.bool.isRequired,
   label: PropTypes.string,
   value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
   paymentDate: PropTypes.string,
   onClose: PropTypes.func.isRequired,
   onConfirmed: PropTypes.func.isRequired,
};
